import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InitialsAvatar } from '@/components/synapse/Avatar';
import { CameraModal } from '@/components/synapse/PhotoCapture';
import { Icon } from '@/components/synapse/Icon';
import { PhotoLightbox } from '@/components/synapse/PhotoLightbox';
import { fileToJpeg, PHOTO_CONTENT_TYPE } from '@/features/patients/photo';
import { cn } from '@/lib/utils';
import type { Patient } from '@/types/patients';
import { usePatientPhotoUpload, useRemovePatientPhoto } from './usePatients';

interface PatientPhotoEditorProps {
  patient: Patient;
  size?: number;
  className?: string;
  /** Tailwind classes for the avatar element (responsive sizing + ring). Defaults
   *  to the compact chart-header size; the registry hero passes a larger size. */
  avatarClassName?: string;
  /** Tailwind classes (size + position) for the camera badge button. */
  badgeClassName?: string;
  /** Icon size inside the camera badge. */
  badgeIconSize?: number;
}

/**
 * Interactive patient avatar for the chart header: shows the photo (or initials)
 * with a small camera badge that opens a take-photo / choose-file / remove menu.
 * Uploads happen immediately (PUT to S3 + confirm); on success the patient query
 * invalidates and the presigned URL replaces the local preview. Failures surface
 * inline and never wedge the control.
 */
export function PatientPhotoEditor({
  patient,
  size = 48,
  className,
  avatarClassName = 'md:h-14 md:w-14',
  badgeClassName = '-bottom-1 -right-1 h-6 w-6',
  badgeIconSize = 13,
}: PatientPhotoEditorProps) {
  const { t } = useTranslation();
  const upload = usePatientPhotoUpload();
  const remove = useRemovePatientPhoto();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const pendingUrlRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState(false);

  // Single owner of the local preview URL: set it (revoking any prior), and the
  // ref lets the unmount/clear paths revoke without re-running effects per value.
  const setPreview = (url: string | null) => {
    if (pendingUrlRef.current && pendingUrlRef.current !== url) {
      URL.revokeObjectURL(pendingUrlRef.current);
    }
    pendingUrlRef.current = url;
    setPendingPreview(url);
  };

  // Drop the local preview only once the refetched patient carries the new photo
  // URL — so the captured image stays on screen straight through to the server
  // URL with no flash back to the old photo/initials in between.
  useEffect(() => {
    setPreview(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient.photoUrl]);

  // Revoke any outstanding preview on unmount.
  useEffect(() => () => setPreview(null), []);

  const busy = upload.isPending || remove.isPending;
  const previewUrl = pendingPreview ?? patient.photoUrl;

  async function doUpload(blob: Blob) {
    setPreview(URL.createObjectURL(blob));
    setError(null);
    try {
      await upload.mutateAsync({
        patientId: patient.id,
        blob,
        contentType: PHOTO_CONTENT_TYPE,
      });
      // Leave the preview in place; the patient.photoUrl effect clears it once
      // the refetch lands the real (presigned) URL.
    } catch {
      setError(t('patients.photo.uploadFailed'));
      setPreview(null);
    }
  }

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      await doUpload(await fileToJpeg(file));
    } catch {
      setError(t('patients.photo.readFailed'));
    }
  }

  async function handleRemove() {
    setMenuOpen(false);
    setError(null);
    try {
      await remove.mutateAsync(patient.id);
    } catch {
      setError(t('patients.photo.removeFailed'));
    }
  }

  return (
    <div className={className}>
      <div className="relative inline-block">
        {/* Tap the photo to enlarge it (same affordance as the registry). With no
            photo there's nothing to view, so the initials render plainly. */}
        {previewUrl ? (
          <button
            type="button"
            onClick={() => setLightbox(true)}
            aria-label={t('patients.photo.view')}
            className="group relative block rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
          >
            <InitialsAvatar
              name={patient.fullName}
              photoUrl={previewUrl}
              size={size}
              className={avatarClassName}
            />
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/0 text-white opacity-0 transition group-hover:bg-black/35 group-hover:opacity-100">
              <Icon name="eye" size={16} />
            </span>
          </button>
        ) : (
          <InitialsAvatar
            name={patient.fullName}
            photoUrl={previewUrl}
            size={size}
            className={avatarClassName}
          />
        )}
        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          disabled={busy}
          aria-label={t('patients.photo.edit')}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          className={cn(
            'absolute flex items-center justify-center rounded-full border border-line bg-paper text-muted shadow-sm hover:bg-paper-2 hover:text-ink disabled:opacity-50',
            badgeClassName,
          )}
        >
          <Icon name="scan" size={badgeIconSize} />
        </button>

        {menuOpen && (
          <>
            {/* Click-away backdrop. */}
            <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
            <div
              role="menu"
              className="absolute left-0 top-[calc(100%+6px)] z-40 w-44 overflow-hidden rounded-lg border border-line bg-paper py-1 shadow-md"
            >
              <MenuItem
                icon="scan"
                label={t('patients.photo.takePhoto')}
                onClick={() => {
                  setMenuOpen(false);
                  setCameraOpen(true);
                }}
              />
              <MenuItem
                icon="plus"
                label={t('patients.photo.choose')}
                onClick={() => {
                  setMenuOpen(false);
                  fileInputRef.current?.click();
                }}
              />
              {patient.photoUrl && (
                <MenuItem icon="trash" label={t('patients.photo.remove')} onClick={handleRemove} />
              )}
            </div>
          </>
        )}
      </div>

      {error && (
        <p className="mt-1 text-[12px] text-destructive" role="alert">
          {error}
        </p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />

      {cameraOpen && (
        <CameraModal
          onClose={() => setCameraOpen(false)}
          onCapture={(blob) => {
            setCameraOpen(false);
            void doUpload(blob);
          }}
        />
      )}

      {lightbox && previewUrl && (
        <PhotoLightbox src={previewUrl} alt={patient.fullName} onClose={() => setLightbox(false)} />
      )}
    </div>
  );
}

function MenuItem({
  icon,
  label,
  onClick,
}: {
  icon: 'scan' | 'plus' | 'trash';
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-paper-2"
    >
      <Icon name={icon} size={14} className="text-muted" />
      {label}
    </button>
  );
}
